import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Button,
  Box,
  Grid,
  CircularProgress,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { deactivateSubscriptionPlans, listSubscriptionPlans } from "../api/subscriptionApi";
import { ROLES, hasRole } from "../utils/auth";

const ManageSubscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const PAGE_LIMIT = 10;

  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole(ROLES.SUPERADMIN)) {
      navigate("/unauthorized", { replace: true });
    }
  }, [navigate]);

  // 🔹 Fetch Plans
  const fetchPlans = async (pageNo = page) => {
    try {
      setLoading(true);
      const res = await listSubscriptionPlans(pageNo, PAGE_LIMIT);

      if (res?.success) {
        // response shape: { data: { total, data: [...], currentPage, totalPages } }
        const payload = res.data;
        const list = Array.isArray(payload) ? payload : payload?.data;

        setPlans(Array.isArray(list) ? list : []);
        setTotalPages(payload?.totalPages || 1);
        setPage(payload?.currentPage || pageNo);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
    }
  };

  // 🔹 Deactivate
  const handleDeactivate = async (id) => {
    try {
      setActionLoading(id);

      const res = await deactivateSubscriptionPlans(id);

      // ✅ check success
      if (res?.success) {
        await fetchPlans(); 
      }

    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box p={3} bgcolor="#f5f5f5" minHeight="100vh">
      {/* 🔹 Top Bar */}
      <Box
        display="grid"
        gridTemplateColumns="1fr auto 1fr"
        alignItems="center"
        mb={3}
      >
        {/* Left */}
        <Box>        
          <Button
            variant="contained"
            onClick={() => navigate("/dashboard")}
            sx={{
              background:
                "linear-gradient(90deg, #7b1fa2 0%, #9c27b0 100%)",
              textTransform: "none",
              px: 3,
            }}
          >
            Back to Dashboard
          </Button>
        </Box>

        {/* Center */}
        <Typography
          variant="h5"
          fontWeight={600}
          textAlign="center"
        >
          Manage Subscription Plans
        </Typography>

        {/* Right (empty for balance) */}
        <Box />
      </Box>

      {/* 🔹 Card Wrapper */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 3,
          minHeight: "calc(100vh - 140px)", // adjust according to your header height
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={8}
          >
            <CircularProgress />
          </Box>
        ) : !plans.length ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" color="text.secondary">
                No subscription plans found
              </Typography>
            </Box>
        ) : (
          <>
          {plans.map((plan) => (
            <Accordion
              key={plan.id}
              sx={{
                mb: 2,
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              {/* HEADER */}
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  alignItems="center"
                >
                  <Typography
                    fontWeight={600}
                    sx={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      maxWidth: "80%", // prevent overlap with chip
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {plan.isActive && (
                    <Chip
                      label="Active"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>
              </AccordionSummary>

              {/* DETAILS */}
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Amount:</strong> ₹{plan.amount}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Period:</strong>{" "}
                      {plan.period?.charAt(0).toUpperCase() + plan.period?.slice(1)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Currency:</strong> {plan.currency}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Tier Rank:</strong> {plan.tierRank}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography
                  mt={2}
                  variant="body2"
                  sx={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <strong>Description:</strong>{" "}
                  {plan.description || "-"}
                </Typography>

                {/* FEATURES */}
                <Box mt={3}>
                  <Typography fontWeight={600} mb={1}>
                    Features
                  </Typography>

                  <Grid container spacing={1}>
                    {Object.entries(plan.features || {}).map(
                      ([key, value]) => (
                        <Grid item xs={12} md={6} key={key}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            px={1}
                            py={0.5}
                            borderBottom="1px solid #eee"
                          >
                            <Typography variant="body2">
                              {key}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {value}
                            </Typography>
                          </Box>
                        </Grid>
                      )
                    )}
                  </Grid>
                </Box>

                {/* ACTION */}
                <Box mt={3} textAlign="right">
                  <Button
                    variant="contained"
                    color="error"
                    disabled={
                      !plan.isActive ||
                      actionLoading === plan.id
                    }
                    onClick={() => handleDeactivate(plan.id)}
                    sx={{ textTransform: "none" }}
                  >
                    {actionLoading === plan.id
                      ? "Deactivating..."
                      : "Deactivate"}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          </>
        )}

        {/* 🔹 Pagination */}
        {plans.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={loading || page === 1}
            >
              Previous
            </button>

            <span> Page {page} of {totalPages} </span>

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={loading || page === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </Paper>
    </Box>
  );
};

export default ManageSubscriptions;